/**
 * Custom hook for managing First Run Experience (FRE) state
 */

import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { createFREClient } from '../api/freApiClient';
import { isTestMode } from '../../config';
import { appInsights } from '../../telemetry/appInsights';

interface UseFREResult {
  /** Whether the FRE should be shown */
  shouldShowFRE: boolean;
  /** Whether the FRE check is still loading */
  loading: boolean;
  /** Whether the FRE is currently open/visible */
  isOpen: boolean;
  /** Open the FRE modal */
  openFRE: () => void;
  /** Replay the FRE (opens without API call on close) */
  replayFRE: () => void;
  /** Close the FRE modal and acknowledge it was shown */
  closeFRE: () => Promise<void>;
}

/**
 * Hook to manage First Run Experience state
 * Checks if FRE should be shown on mount and provides methods to open/close
 */
export const useFRE = (): UseFREResult => {
  const msalContext = useMsal();

  const instance = isTestMode ? null : msalContext?.instance;
  const accounts = isTestMode ? [] : msalContext?.accounts || [];
  const account = accounts[0] || null;

  const [shouldShowFRE, setShouldShowFRE] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isReplay, setIsReplay] = useState<boolean>(false);

  // Check FRE status on mount
  useEffect(() => {
    const checkFREStatus = async () => {
      // Don't check until we have an account (unless in test mode)
      if (!isTestMode && !account) {
        setLoading(false);
        return;
      }

      try {
        const client = createFREClient(instance, account);
        const response = await client.shouldShowFRE();

        setShouldShowFRE(response.shouldShowFRE);

        // Auto-open FRE if it should be shown
        if (response.shouldShowFRE) {
          setIsOpen(true);
        }

        appInsights.trackEvent({
          name: 'FRE_StatusChecked',
          properties: {
            shouldShowFRE: String(response.shouldShowFRE),
          },
        });
      } catch (error) {
        console.error('Error checking FRE status:', error);
        appInsights.trackException({
          exception: error instanceof Error ? error : new Error(String(error)),
          severityLevel: SeverityLevel.Warning,
          properties: {
            component: 'useFRE',
            action: 'checkFREStatus',
          },
        });
        // On error, don't show FRE
        setShouldShowFRE(false);
      } finally {
        setLoading(false);
      }
    };

    checkFREStatus();
  }, [instance, account]);

  const openFRE = useCallback(() => {
    setIsReplay(false);
    setIsOpen(true);
    appInsights.trackEvent({
      name: 'FRE_Opened',
    });
  }, []);

  const replayFRE = useCallback(() => {
    setIsReplay(true);
    setIsOpen(true);
    appInsights.trackEvent({
      name: 'FRE_Replayed',
    });
  }, []);

  const closeFRE = useCallback(async () => {
    setIsOpen(false);

    // Only acknowledge FRE if it's not a replay
    if (isReplay) {
      setIsReplay(false);
      appInsights.trackEvent({
        name: 'FRE_ReplayClosed',
      });
      return;
    }

    // Acknowledge FRE was shown
    try {
      const client = createFREClient(instance, account);
      await client.acknowledgeFRE();

      setShouldShowFRE(false);

      appInsights.trackEvent({
        name: 'FRE_Completed',
      });
    } catch (error) {
      console.error('Error acknowledging FRE:', error);
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error)),
        severityLevel: SeverityLevel.Warning,
        properties: {
          component: 'useFRE',
          action: 'acknowledgeFRE',
        },
      });
    }
  }, [instance, account, isReplay]);

  return {
    shouldShowFRE,
    loading,
    isOpen,
    openFRE,
    replayFRE,
    closeFRE,
  };
};
